/**
 * Der Knopf **„Muster daraus machen"** am einzelnen Befund (ADR-0033, das
 * Ziel, um dessentwillen das Muster in die Daten gezogen ist; ADR-0012).
 *
 * Er steht dort, wo ein Mensch gerade über den Befund geurteilt hat: Wer eine
 * Form für wichtig genug hält, um sie zu qualifizieren, soll sie im selben
 * Augenblick zu einem Muster machen können — und nicht beim nächsten Ausrollen.
 *
 * **Nur für `admin`.** Das Anlegen ist eine Schreiboperation an der
 * Musterverwaltung; wer sie nicht hat, sieht den Knopf nicht. Das ist eine Frage
 * der Anzeige und keine Rechteentscheidung — die trifft der Server bei jeder
 * Anfrage.
 *
 * **Nicht am Kontrollbefund.** Er *ist* schon ein Muster; ein Muster aus ihm wäre
 * dasselbe ein zweites Mal. Dieselbe Grenze zieht das Nachhalten am Befund
 * (ADR-0012).
 *
 * **Der Entwurf kommt vom Server**, samt dem Codeausschnitt der Stelle: Die
 * Ansicht setzt keine Vorbelegung zusammen, die sie nur zur Hälfte hat — der
 * Befundverlauf zeigt einen gedeckelten Ausschnitt mit Zusammenhang, und das
 * Muster soll die Zeilen des Befundes tragen und nicht die daneben.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { Button, Dialog, IconButton, Modal } from "../../design-system";
import type { NewControlPattern, PatternDraft, PatternFromFindingApi } from "../../api/client";
import { Notice, describe } from "../loading";
import { NewPatternDialog } from "./NewPatternDialog";

export function PatternFromFinding({
  client,
  findingId,
  onCreated,
}: {
  client: PatternFromFindingApi;
  findingId: number;
  /** Angelegt — die Fläche darf sich neu holen, was sie zeigt. */
  onCreated?: () => void;
}): ReactElement {
  const [draft, setDraft] = useState<PatternDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const open = (): void => {
    // Zweimal klicken, während der Entwurf noch unterwegs ist, holt ihn nicht
    // zweimal. Der Baustein kennt kein `disabled`, also steht die Sperre hier.
    if (busy) {
      return;
    }

    setBusy(true);
    setFailure(null);

    client.controlPatternDraft(findingId).then(
      (loaded) => {
        setDraft(loaded);
        setBusy(false);
      },
      (error: unknown) => {
        setFailure(describe(error));
        setBusy(false);
      },
    );
  };

  return (
    <>
      <IconButton
        icon="scan-search"
        tip="Muster daraus machen"
        size="sm"
        tone="quiet"
        onClick={open}
      />

      {draft === null ? null : (
        <NewPatternDialog
          kinds={draft.kinds}
          severities={draft.severities}
          from={draft}
          create={(pattern: NewControlPattern) => client.createControlPattern(pattern)}
          close={() => setDraft(null)}
          created={() => {
            setDraft(null);
            onCreated?.();
          }}
        />
      )}

      {/* Ein Entwurf, der nicht kommt, wird gesagt und nicht verschluckt: Sonst
          bliebe ein Klick ohne Wirkung, und niemand wüsste, woran es lag. */}
      {failure === null ? null : (
        <Modal onClose={() => setFailure(null)}>
          <Dialog
            title="Kein Entwurf"
            description="Die Stelle, auf die dieser Befund zeigt, ließ sich nicht holen."
            width={560}
            onClose={() => setFailure(null)}
            footer={
              <Button variant="outline" size="lg" onClick={() => setFailure(null)}>
                Schließen
              </Button>
            }
          >
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          </Dialog>
        </Modal>
      )}
    </>
  );
}
